/**
 * RawImage — intermediate pixel buffer between image loading and PDF embedding.
 *
 * Pipeline:
 *   File / URL / WebUpload / Filesystem
 *     → RawImage.fromBytes() / fromUrl() / fromFile() / fromPath()
 *     → RawImage { width, height, canvas: IImageCanvas }
 *     → .toShadow() / .flipH() / .toBlackWhite() / .toOneColor() / .toGradient()  [sync, return new RawImage]
 *     → .embed(pdf: PdfGen) : Promise<PdfImage>
 *     → PdfImage → page.drawImageFit(pdfImage, rect)
 */

import { IImageCanvas, getImageCanvasLoader } from './imageCanvas';
import type { PdfGen, PdfImage } from './pdf';

export class RawImage {
  constructor(private readonly canvas: IImageCanvas) {}

  get width(): number {
    return this.canvas.width;
  }
  get height(): number {
    return this.canvas.height;
  }

  /** Get the raw RGBA pixel buffer. */
  getRgba(): Uint8Array {
    return this.canvas.getRgba();
  }

  // ---- Sync filters — each returns a new RawImage ----

  /** Set all opaque pixels (alpha > 10) to black. */
  toShadow(): RawImage {
    return this._apply((p) => {
      for (let i = 0; i < p.length; i += 4)
        if (p[i + 3] > 10) {
          p[i] = 0;
          p[i + 1] = 0;
          p[i + 2] = 0;
        }
    });
  }

  /** Flip image horizontally (left ↔ right). */
  flipH(): RawImage {
    return this._apply((p) => {
      const { width, height } = this.canvas;
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < Math.floor(width / 2); x++) {
          const li = (y * width + x) * 4;
          const ri = (y * width + (width - 1 - x)) * 4;
          for (let c = 0; c < 4; c++) {
            const t = p[li + c];
            p[li + c] = p[ri + c];
            p[ri + c] = t;
          }
        }
      }
    });
  }

  /** Flip image vertically (top ↔ bottom). */
  flipV(): RawImage {
    return this._apply((p) => {
      const { width, height } = this.canvas;
      for (let y = 0; y < Math.floor(height / 2); y++) {
        for (let x = 0; x < width; x++) {
          const ti = (y * width + x) * 4;
          const bi = ((height - 1 - y) * width + x) * 4;
          for (let c = 0; c < 4; c++) {
            const t = p[ti + c];
            p[ti + c] = p[bi + c];
            p[bi + c] = t;
          }
        }
      }
    });
  }

  /** Convert to grayscale (preserves alpha). */
  toBlackWhite(): RawImage {
    return this._apply((p) => {
      for (let i = 0; i < p.length; i += 4) {
        const gray = Math.round(0.299 * p[i] + 0.587 * p[i + 1] + 0.114 * p[i + 2]);
        p[i] = gray;
        p[i + 1] = gray;
        p[i + 2] = gray;
      }
    });
  }

  /** Recolor all opaque pixels (alpha > 10) to a single RGB color. */
  toOneColor(r: number, g: number, b: number): RawImage {
    return this._apply((p) => {
      for (let i = 0; i < p.length; i += 4)
        if (p[i + 3] > 10) {
          p[i] = r;
          p[i + 1] = g;
          p[i + 2] = b;
        }
    });
  }

  /**
   * Apply a horizontal left→right color gradient to opaque pixels (alpha > 220).
   * Includes vertical brightness modulation: top brighter, bottom darker.
   * Transparent pixels (alpha ≤ 220) become fully transparent.
   */
  toGradient(
    colorLeft: { r: number; g: number; b: number },
    colorRight: { r: number; g: number; b: number }
  ): RawImage {
    const { width, height } = this.canvas;
    return this._apply((p) => {
      for (let y = 0; y < height; y++) {
        const d = (y / height - 0.5) * 100;
        for (let x = 0; x < width; x++) {
          const idx = (y * width + x) * 4;
          if (p[idx + 3] > 220) {
            const t = x / width;
            p[idx] = Math.min(
              255,
              Math.max(0, Math.round(colorLeft.r + (colorRight.r - colorLeft.r) * t - d))
            );
            p[idx + 1] = Math.min(
              255,
              Math.max(0, Math.round(colorLeft.g + (colorRight.g - colorLeft.g) * t - d))
            );
            p[idx + 2] = Math.min(
              255,
              Math.max(0, Math.round(colorLeft.b + (colorRight.b - colorLeft.b) * t - d))
            );
            p[idx + 3] = 255;
          } else {
            p[idx + 3] = 0;
          }
        }
      }
    });
  }

  private _apply(fn: (pixels: Uint8Array) => void): RawImage {
    const pixels = new Uint8Array(this.canvas.getRgba());
    fn(pixels);
    return new RawImage(this.canvas.withRgba(pixels));
  }

  // ---- Embed into PDF (final step) ----

  /**
   * Encode pixels to PNG and embed into the PDF document.
   * Returns a PdfImage ready for drawing. The decoded canvas is stored
   * so that further PdfImage filter calls (toShadow etc.) work.
   */
  async embed(pdf: PdfGen): Promise<PdfImage> {
    const embedded = await pdf.pdf.embedPng(this.canvas.toPng());
    // Import PdfImage lazily to avoid circular dependency
    const { PdfImage } = await import('./pdf');
    return new PdfImage(embedded, embedded.width, embedded.height, this.canvas);
  }

  // ---- Factory methods (require a registered IImageCanvasLoader) ----

  /** Decode from raw bytes (PNG or JPEG, auto-detected by loader). */
  static async fromBytes(bytes: Uint8Array): Promise<RawImage> {
    return new RawImage(await getImageCanvasLoader().fromBytes(bytes));
  }

  /** Fetch from a URL and decode. */
  static async fromUrl(url: string): Promise<RawImage> {
    return new RawImage(await getImageCanvasLoader().fromUrl(url));
  }

  /** Load from a filesystem path (Node.js only). */
  static async fromPath(path: string): Promise<RawImage> {
    return new RawImage(await getImageCanvasLoader().fromPath(path));
  }

  /** Load from a File object (browser only). */
  static async fromFile(file: File): Promise<RawImage> {
    return new RawImage(await getImageCanvasLoader().fromFile(file));
  }

  /** Wrap an existing IImageCanvas directly. */
  static fromCanvas(canvas: IImageCanvas): RawImage {
    return new RawImage(canvas);
  }
}
